import { type FC, useState } from "react"
import { useLocation } from "react-router"
import styled from "styled-components"
import { css } from "styled-components"
import tinycolor from "tinycolor2"

import Tooltip from "@forento/shared-client/components/Tooltip"
import { type PurchaseableType } from "@forento/shared/models/product"
import { toPriceString } from "@forento/shared/utilities/currency"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/PlatformContext"
import { useUser } from "~/contexts/UserContext"
import { CloseIcon } from "~/themes/original/components/Icon"
import Modal, { ModalButtons, ModalText, ModalTitle } from "~/themes/original/components/Modal"
import { useTranslation } from "~/translations"
import { isColorDark } from "~/utilities/color"
import routes from "~/utilities/routes"
import { dangerColor } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

import Button from "../Button"
import PayPalButtons from "./PayPalButtons"

type Props = {
	item: {
		id: number
		type: PurchaseableType
		name: string
		shortDescription: string | null
		price: { amount: number; currency: string }
		accessTimeMonths: number | null
	}
	onPurchase(args: { paypalOrder: { userStatus: "existing" | "assigned" | "created"; email: string } }): Promise<void>
}
const PurchaseItem: FC<Props> = ({ item, onPurchase }) => {
	const user = useUser().user
	const platform = usePlatform().platform
	const location = useLocation()
	const t = useTranslation()
	const alert = useAlert()

	const [discount, setDiscount] = useState<{
		code: string
		name: string
		discount: { type: "percent" | "amount"; value: number }
	}>()
	const [isPaypalModalOpen, setPaypalModalOpen] = useState(false)

	const createStripeOrder = query.payment.stripe.createOrder.useMutation({
		onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
		async onError(error) {
			console.error(error)
			await alert.show("Error", "An error occurred while creating the order.")
		},
	})

	const buyLabel =
		item.type === "event" ? `Buy ticket — ${toPriceString(item.price)}` : `Buy — ${toPriceString(item.price)}`

	return (
		<>
			<Modal isOpen={isPaypalModalOpen}>
				<ModalTitle>{item.name}</ModalTitle>
				{item.shortDescription !== null && <ModalText>{item.shortDescription}</ModalText>}
				{discount ? (
					<DiscountCode>
						{discount.name} -{" "}
						{discount.discount.type === "percent"
							? `${discount.discount.value * 100}%`
							: toPriceString({ amount: discount.discount.value, currency: platform.currency })}
						<RemoveDiscountCodeButton onClick={() => setDiscount(undefined)}>
							<RemoveDiscountCodeButtonIcon />
						</RemoveDiscountCodeButton>
					</DiscountCode>
				) : (
					<Button
						onClick={async () => {
							const dialog = await alert.input(
								"Discount code",
								"Enter your discount code here",
								"Discount code",
							)
							if (!dialog.result) return

							const validation = await trpc.payment.discountCode.validatePaypal.mutate(dialog.result)
							switch (validation.status) {
								case "invalid": {
									await alert.show("Invalid code", "That code is invalid.")
									break
								}
								case "expired": {
									await alert.show("Expired code", "That code has expired.")
									break
								}
								case "max-redemptions": {
									await alert.show(
										"Max redemptions reached",
										"That code has reached its maximum number of redemptions.",
									)
									break
								}
								case "valid": {
									setDiscount(validation.code)
									dialog.close()
									break
								}
								default: {
									exhaustiveGuard(validation)
								}
							}
						}}
					>
						Apply discount code
					</Button>
				)}
				<ModalButtons>
					<Button variant="secondary" onClick={() => setPaypalModalOpen(false)}>
						{t("close")}
					</Button>
					<PayPalButtons onPurchase={onPurchase} item={item} discountCode={discount?.code ?? null} />
				</ModalButtons>
			</Modal>

			<PurchaseContainer>
				{!platform.paymentGateway?.isEnabled ? (
					<Tooltip tooltip="Payments are not enabled">
						<Button variant="primary" isDisabled>
							{buyLabel}
						</Button>
					</Tooltip>
				) : platform.paymentGateway.type === "stripe" ? (
					<Button
						variant="primary"
						onClick={() => createStripeOrder.mutate({ item })}
						isLoading={createStripeOrder.isPending}
					>
						{buyLabel}
					</Button>
				) : platform.paymentGateway.type === "paypal" ? (
					platform.paymentGateway.hasDiscountCodes ? (
						<Button variant="primary" onClick={() => setPaypalModalOpen(true)}>
							{buyLabel}
						</Button>
					) : (
						<Container>
							<PriceDisplay>
								<PriceLabel>Price</PriceLabel>
								<Price>{toPriceString(item.price)}</Price>
							</PriceDisplay>
							<PayPalButtons onPurchase={onPurchase} item={item} discountCode={null} />
						</Container>
					)
				) : (
					exhaustiveGuard(platform.paymentGateway)
				)}

				{user === null && (
					<SignInSection>
						<SignInDivider>
							<SignInDividerText>or</SignInDividerText>
						</SignInDivider>
						<Button
							variant="bordered"
							onClick={`${routes.user.signin()}?next=${encodeURIComponent(location.pathname)}`}
						>
							{t("user.signIn.title")}
						</Button>
					</SignInSection>
				)}

				{item.accessTimeMonths !== null && (
					<AccessTimeInfo>
						<AccessTimeIcon>⏰</AccessTimeIcon>
						<AccessTimeText>
							{t("course.accessTimeMonths", { count: item.accessTimeMonths })}
						</AccessTimeText>
					</AccessTimeInfo>
				)}
			</PurchaseContainer>
		</>
	)
}

const DiscountCode = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

const RemoveDiscountCodeButton = styled(Button)`
	width: 24px;
	height: 24px;
`

const RemoveDiscountCodeButtonIcon = styled(CloseIcon)`
	color: ${dangerColor};
	width: 100%;
	height: 100%;
`

const PurchaseContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 100%;
	max-width: 400px;
`

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	width: 100%;
`

const PriceDisplay = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 20px;
	background: linear-gradient(135deg, ${props => props.theme.primaryColor}15, ${props => props.theme.primaryColor}25);
	border: 2px solid ${props => props.theme.primaryColor}30;
	border-radius: 12px;
	backdrop-filter: blur(10px);
`

const PriceLabel = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`

const Price = styled.div`
	font-weight: 700;
	font-size: 24px;
	text-align: center;
	padding: 12px 20px;
	border-radius: 10px;
	box-shadow: 0 4px 12px ${props => props.theme.primaryColor}20;

	${props => {
		const priceBackground = tinycolor(props.theme.primaryColor).lighten(8).toString()
		return css`
			color: ${isColorDark(priceBackground) ? "#f9fafb" : "#111827"};
			background-color: ${priceBackground};
		`
	}}
`

const SignInSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
`

const SignInDivider = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	gap: 16px;

	&::before {
		content: "";
		flex: 1;
		height: 1px;
		background: linear-gradient(90deg, transparent, #d1d5db, transparent);
	}

	&::after {
		content: "";
		flex: 1;
		height: 1px;
		background: linear-gradient(90deg, transparent, #d1d5db, transparent);
	}
`

const SignInDividerText = styled.span`
	font-size: 12px;
	color: #6b7280;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`

const AccessTimeInfo = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 12px 16px;
	background: linear-gradient(135deg, #fef3c7, #fde68a);
	border: 1px solid #f59e0b;
	border-radius: 10px;
	box-shadow: 0 2px 8px #f59e0b15;
`

const AccessTimeIcon = styled.span`
	font-size: 16px;
`

const AccessTimeText = styled.p`
	margin: 0;
	font-size: 12px;
	font-weight: 500;
	color: #92400e;
	text-align: center;
`

export default PurchaseItem
